import { Users } from 'lucide-react';
import type { ConversationWithRelations } from '@/lib/types';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { statusBadge, leadStatusBadge } from '@/lib/badges';
import { vistoSinResponder } from '@/lib/visto-sin-responder';
import { conversationTime } from '@/lib/format';
import { getContactDisplayName, getContactSubtitle } from '@/lib/contact-display';
import { cn } from '@/lib/cn';
import { anuncioDelContacto } from '@/lib/anuncio';

/**
 * Ítem de la lista de conversaciones. Muestra nombre + número del contacto para
 * diferenciar contactos con pushName parecido (FIX v0.4). Componente puro (sin
 * hooks) para que sea fácilmente testeable de forma aislada.
 */
export function ConversationItem({
  conversation,
  selected,
  onSelect,
}: {
  conversation: ConversationWithRelations;
  selected?: boolean;
  onSelect?: () => void;
}) {
  const c = conversation;
  const badge = statusBadge(c.status);
  const lead = c.leadStatus ? leadStatusBadge(c.leadStatus) : null;
  // "Atención" se resalta (acento ámbar) para que salte a la vista: estas
  // conversaciones, además, se ordenan arriba en la lista.
  const isWaitingHuman = c.status === 'WAITING_HUMAN';
  const isGroup = c.contact?.isGroup ?? false;
  const name = getContactDisplayName(c.contact);
  // Número junto al nombre para diferenciar contactos con nombre parecido.
  const subtitle = getContactSubtitle(c.contact);
  const last = c.messages?.[0];

  return (
    <button
      onClick={onSelect}
      className={cn(
        // 21-oct, macOS 27: fila de lista con radio 8; la elegida en gris (#E6E6E6 / #323232).
        'flex w-full items-start gap-3 rounded-[8px] px-3 py-2.5 text-left transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.04]',
        isWaitingHuman && 'border-l-[3px] border-amber-400 bg-amber-50 dark:bg-amber-900/20',
        selected && 'bg-slate-200 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-800',
      )}
    >
      <Avatar
        name={name}
        src={c.contact?.avatarUrl}
        isGroup={isGroup}
        seed={c.contact?.externalId}
        size="md"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="flex min-w-0 items-center gap-1 truncate text-sm font-medium text-slate-900 dark:text-slate-100">
            {isGroup && <Users className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
            <span className="truncate">{name}</span>
          </p>
          <span className="shrink-0 text-[11px] text-slate-400">
            {conversationTime(c.lastMessageAt ?? c.createdAt)}
          </span>
        </div>
        {subtitle && (
          <p className="mt-0.5 truncate text-[11px] text-slate-400 dark:text-slate-500">
            {subtitle}
          </p>
        )}
        <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
          {last?.content ??
            (isGroup && c.contact?.memberCount
              ? `${c.contact.memberCount} miembros`
              : c.channel?.name ?? '—')}
        </p>
        <div className="mt-1 flex items-center gap-1.5">
          {!isGroup && anuncioDelContacto(c.contact) && (
            <Badge variant="success" title="Llegó por un anuncio de Facebook o Instagram">
              Anuncio
            </Badge>
          )}
          {lead && <Badge variant={lead.variant}>{lead.label}</Badge>}
          <Badge variant={badge.variant}>{badge.label}</Badge>
          {/* Indicador INTERNO "visto sin responder": alguien lo abrió y nadie
              contestó (bot apagado / modo humano). No toca WhatsApp. */}
          {vistoSinResponder(c) && (
            <span
              title="Visto sin responder"
              className="ml-auto flex items-center gap-1 text-[11px] font-medium text-amber-600 dark:text-amber-400"
            >
              <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden />
              Visto sin responder
            </span>
          )}
          {c.unreadCount > 0 && (
            <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-acento px-1.5 text-[11px] font-semibold text-white">
              {c.unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
